import { call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects'
import { IRootState } from '../../../store'
import { isOffline } from '../../../utilities'
import { NotificationCenterConstants } from '../store/notificationCenter.constants'
import {
  setPolledNotificationIds,
  setViewedNotificationIds,
  stopNotificationPolling,
} from '../store/notificationCenter.actions'
import { ConfigurationsConstants } from '../../configurations/store/configurations.constants'
import { UserConstants } from '../../user/store/user.constants'
import { NotificationCenterService } from '../services/notificationCenter.service'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { INotificationIdsResponse } from '../store/notificationCenter.types'
import Logger from '../../../services/logger.service'
import LocalStorageService from '../../../services/localStorage.service'
import { getUserSelector } from '../../user/selectors'
import { getNotificationCenterSelector } from '../selectors'
import { INotificationConfig } from '../../configurations/store/configurations.types'
import { getNotificationConfigSelector } from '../selectors/notificationCenter.selectors'

function* pollTick(): Generator<any, boolean, any> {
  if (isOffline()) return false

  try {
    const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
    const service = new NotificationCenterService(authenticationJwt)
    const response: INotificationIdsResponse = yield call([service, service.fetchNotificationIds])

    if (response.status === 401) {
      Logger.warn('Notification polling: received 401 — unauthorized')
      return true
    }

    if (response.status === 200 && Array.isArray(response.data)) {
      const incomingIds = response.data as number[]
      const { polledNotificationIds } = yield select(getNotificationCenterSelector)

      const hasChanged =
        incomingIds.length !== polledNotificationIds.length ||
        [...incomingIds].sort((a, b) => a - b).join() !== [...polledNotificationIds].sort((a, b) => a - b).join()

      if (hasChanged) {
        yield put(setPolledNotificationIds(incomingIds))
        Logger.info(`Notification polling: ${incomingIds.length} notification(s) found`)
      }
    }
  } catch (e: any) {
    Logger.warn(`Notification polling error: ${e?.message ?? 'unknown'}`)
  }
  return false
}

function* pollLoop() {
  let consecutiveUnauthorized = 0

  while (true) {
    const config: INotificationConfig = yield select(getNotificationConfigSelector)
    const isUnauthorized: boolean = yield call(pollTick)

    if (isUnauthorized) {
      consecutiveUnauthorized++
      Logger.warn(`Notification polling: 401 attempt ${consecutiveUnauthorized}/${config.retryCount}`)
      if (consecutiveUnauthorized >= config.retryCount) {
        Logger.error(`Notification polling: stopping after ${config.retryCount} consecutive 401 errors`)
        yield put(stopNotificationPolling())
        break
      }
    } else {
      consecutiveUnauthorized = 0
    }

    yield delay(config.minIntervalMinutes * 60 * 1000)
  }
}

function* loadViewedNotificationIds() {
  try {
    const { userId, databaseUserName } = yield select(getUserSelector)
    const stored = LocalStorageService.getItemForUserIdElseDatabaseUserName(
      userId,
      databaseUserName,
      'viewedNotificationIds'
    )
    const ids: number[] = stored ? JSON.parse(stored) : []
    yield put(setViewedNotificationIds(ids))
  } catch (e: any) {
    Logger.warn(`loadViewedNotificationIds error: ${e?.message ?? 'unknown'}`)
  }
}

function* runPollCycle() {
  const hasSettings: boolean = yield select(
    (state: IRootState) => (state.configurations?.appSettingsEntries?.length ?? 0) > 0
  )
  if (!hasSettings) {
    yield take(ConfigurationsConstants.SET_APP_SETTINGS_ENTRIES)
  }
  yield call(loadViewedNotificationIds)
  yield race({
    task: call(pollLoop),
    cancel: take(NotificationCenterConstants.STOP_NOTIFICATION_POLLING),
  })
}

export function* startNotificationPollingSaga() {
  yield takeLatest(NotificationCenterConstants.START_NOTIFICATION_POLLING, runPollCycle)
}

export function* stopNotificationPollingOnLogoutSaga() {
  yield takeLatest(UserConstants.RESET_USER, function* () {
    yield put(stopNotificationPolling())
  })
}
